import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ArrowLeft } from "lucide-react";

const fmtAmt = (n, cur = "TRY") => (cur === "TRY" ? fmtTL(n) : `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${cur}`);

const dimText = (it) => {
  if (it.size) return it.size;
  const parts = [it.thickness, it.width, it.height, it.length].filter((v) => Number(v) > 0);
  return parts.length ? parts.join("×") + " mm" : "";
};
const kgText = (it) => {
  const kg = Number(it.total_kg || it.kilogram || 0);
  return kg > 0 ? kg.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " kg" : "";
};
const miktarText = (it) => {
  const kg = Number(it.total_kg || it.kilogram || 0);
  if (it.calc_type && kg > 0) return kg.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " kg";
  return `${Number(it.quantity || 0).toLocaleString("tr-TR")} ${it.unit || ""}`;
};

export default function CustomerStatement() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [settings, setSettings] = useState(null);
  const from = sp.get("from") || "";
  const to = sp.get("to") || "";

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setD(r.data));
    api.get("/settings/company").then((r) => setSettings(r.data));
  }, [id]);

  const { rows, opening, totDebit, totCredit, closing } = useMemo(() => {
    if (!d) return { rows: [], opening: 0, totDebit: 0, totCredit: 0, closing: 0 };
    const all = d.ledger || [];
    const before = all.filter((e) => from && e.date.slice(0, 10) < from);
    const opening = before.reduce((s, e) => s + e.debit - e.credit, 0);
    const rows = all.filter((e) => (!from || e.date.slice(0, 10) >= from) && (!to || e.date.slice(0, 10) <= to));
    const totDebit = rows.reduce((s, e) => s + e.debit, 0);
    const totCredit = rows.reduce((s, e) => s + e.credit, 0);
    return { rows, opening, totDebit, totCredit, closing: opening + totDebit - totCredit };
  }, [d, from, to]);

  if (!d || !settings) return <div className="p-10 text-center text-muted-foreground">Yükleniyor...</div>;
  const c = d.customer;
  const setRange = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); };
  const period = from || to ? `${from ? fmtDate(from) : "Başlangıç"} - ${to ? fmtDate(to) : "Bugün"}` : "Tüm hareketler";
  const th = { padding: "7px 8px", fontSize: "8.5pt", textTransform: "uppercase", letterSpacing: "0.5px", color: "#fff", background: "#1e293b", textAlign: "left" };
  const td = { padding: "6px 8px", borderBottom: "1px solid #e2e8f0", fontSize: "9.5pt", verticalAlign: "top" };
  const R = { textAlign: "right" };

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white print:p-0" data-testid="customer-statement-page">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print { .no-print { display: none !important; } body { background: white !important; } .a4-page { box-shadow: none !important; margin: 0 !important; } tr { page-break-inside: avoid; } thead { display: table-header-group; } }
        .a4-page { width: 210mm; min-height: 297mm; padding: 15mm 15mm 20mm; margin: 20px auto; background: white; color: #0f172a; box-shadow: 0 6px 30px rgba(0,0,0,0.15); font-family: 'Inter', system-ui, sans-serif; font-size: 10.5pt; line-height: 1.45; }
        .a4-page .num { font-family: 'JetBrains Mono', monospace; font-feature-settings: 'tnum'; }
      `}</style>
      <div className="no-print sticky top-0 z-10 bg-slate-900 text-white px-5 py-3 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <button onClick={() => window.close()} className="flex items-center gap-2 text-sm hover:opacity-80"><ArrowLeft size={16} /> Kapat</button>
        <div className="flex items-center gap-2 text-sm">
          <span>Tarih aralığı:</span>
          <Input type="date" value={from} onChange={(e) => setRange("from", e.target.value)} className="h-8 w-40 bg-slate-800 border-slate-700 text-white" data-testid="statement-from" />
          <span>—</span>
          <Input type="date" value={to} onChange={(e) => setRange("to", e.target.value)} className="h-8 w-40 bg-slate-800 border-slate-700 text-white" data-testid="statement-to" />
        </div>
        <Button onClick={() => window.print()} data-testid="statement-print-btn"><Printer size={16} className="mr-2" /> Yazdır / PDF Olarak Kaydet</Button>
      </div>

      <div className="a4-page">
        <div className="flex justify-between items-start gap-6 pb-5 border-b-2" style={{ borderColor: "#E05A47" }}>
          <div className="flex-1">
            {settings.logo_url && <img src={settings.logo_url} alt="Logo" style={{ maxHeight: 60, marginBottom: 10 }} />}
            <div style={{ fontSize: "14pt", fontWeight: 800 }}>{settings.name}</div>
            <div style={{ fontSize: "8.5pt", color: "#334155", lineHeight: 1.7 }}>
              {settings.address && <div>{settings.address}</div>}
              <div>{settings.phone && <>Tel: {settings.phone}</>}{settings.email && <> · {settings.email}</>}{settings.website && <> · {settings.website}</>}</div>
              <div>{settings.tax_office && <>V.D: {settings.tax_office}</>}{settings.tax_no && <> · V.No: {settings.tax_no}</>}</div>
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 220 }}>
            <div style={{ fontSize: "22pt", fontWeight: 900, letterSpacing: "-1px", color: "#E05A47", lineHeight: 1 }}>CARİ HESAP EKSTRESİ</div>
            <div style={{ marginTop: 12, fontSize: "9pt" }}>
              <div><span style={{ color: "#64748b" }}>Tarih:</span> <span className="num">{period}</span></div>
              <div><span style={{ color: "#64748b" }}>Düzenleme:</span> <span className="num">{fmtDate(new Date().toISOString())}</span></div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, background: "#f1f5f9", borderRadius: 6, padding: "12px 16px", borderLeft: "3px solid #E05A47" }}>
          <div style={{ fontSize: "8pt", textTransform: "uppercase", letterSpacing: "1.5px", color: "#64748b", fontWeight: 700, marginBottom: 4 }}>Firma</div>
          <div style={{ fontSize: "12pt", fontWeight: 800 }} data-testid="statement-customer-name">{c.name}</div>
          <div style={{ fontSize: "9pt", color: "#334155", lineHeight: 1.6 }}>
            {c.contact && <div>Yetkili: {c.contact}</div>}
            <div>{c.phone && <>Tel: {c.phone}</>}{c.email && <> · {c.email}</>}</div>
            {c.address && <div>{c.address}</div>}{c.tax_no && <div>V.No: {c.tax_no}</div>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3" style={{ marginTop: 16 }}>
          {[["Toplam Borç", totDebit, "#0f172a"], ["Toplam Alacak", totCredit, "#059669"], ["Güncel Bakiye", closing, closing > 0 ? "#E05A47" : "#059669"]].map(([l, v, col]) => (
            <div key={l} style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ fontSize: "8pt", textTransform: "uppercase", color: "#64748b", fontWeight: 700 }}>{l}</div>
              <div className="num" style={{ fontSize: "13pt", fontWeight: 800, color: col }} data-testid={`statement-${l === "Güncel Bakiye" ? "balance" : l === "Toplam Borç" ? "debit" : "credit"}`}>{fmtAmt(v)}</div>
            </div>
          ))}
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }} data-testid="statement-table">
          <thead><tr>
            <th style={th}>Tarih</th>
            <th style={th}>Ürün / Açıklama</th>
            <th style={{ ...th, ...R }}>Miktar</th>
            <th style={{ ...th, ...R }}>Birim Fiyat</th>
            <th style={{ ...th, ...R }}>Tutar</th>
          </tr></thead>
          <tbody>
            {from && <tr style={{ background: "#f8fafc" }}><td style={td} className="num">{fmtDate(from)}</td><td style={{ ...td, fontStyle: "italic" }} colSpan={3}>Önceki Dönemden Devir</td><td style={{ ...td, ...R, fontWeight: 700 }} className="num">{fmtAmt(opening)}</td></tr>}
            {(() => {
              const sorted = [...rows].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
              const out = [];
              let gi = 0;
              while (gi < sorted.length) {
                const day = (sorted[gi].date || "").slice(0, 10);
                const group = [];
                while (gi < sorted.length && (sorted[gi].date || "").slice(0, 10) === day) { group.push(sorted[gi]); gi++; }
                let firstRow = true;
                let dayDebit = 0;
                const BB = "3px solid #0f172a";
                group.forEach((e, ei) => {
                  const its = e.type === "satis" ? (e.items || []) : [];
                  dayDebit += Number(e.debit) || 0;
                  if (its.length > 0) {
                    its.forEach((it, j) => {
                      out.push(
                        <tr key={e.ref + "-" + j}>
                          <td style={td} className="num">{firstRow ? fmtDate(day) : ""}</td>
                          <td style={td}>{it.product_name}{j === 0 && e.note ? <span style={{ fontSize: "8pt", color: "#64748b" }}> — {e.note}</span> : ""}</td>
                          <td style={{ ...td, ...R }} className="num">{miktarText(it)}</td>
                          <td style={{ ...td, ...R }} className="num">{fmtAmt(it.unit_price)}</td>
                          <td style={{ ...td, ...R, fontWeight: 600 }} className="num">{fmtAmt(it.total)}</td>
                        </tr>
                      );
                      firstRow = false;
                    });
                    if (e.credit > 0) {
                      out.push(
                        <tr key={e.ref + "-pay"}>
                          <td style={td}></td>
                          <td style={td} colSpan={3}>Tahsilat</td>
                          <td style={{ ...td, ...R, color: "#059669", fontWeight: 600 }} className="num">- {fmtAmt(e.credit)}</td>
                        </tr>
                      );
                    }
                  } else {
                    const isCredit = (e.credit || 0) > 0 && !(e.debit > 0);
                    out.push(
                      <tr key={e.ref + "-" + ei}>
                        <td style={td} className="num">{firstRow ? fmtDate(day) : ""}</td>
                        <td style={td} colSpan={3}>{e.desc}{e.note ? <span style={{ fontSize: "8pt", color: "#64748b" }}> — {e.note}</span> : ""}</td>
                        <td style={{ ...td, ...R, fontWeight: 600, color: isCredit ? "#059669" : "#0f172a" }} className="num">{isCredit ? `- ${fmtAmt(e.credit)}` : fmtAmt(e.debit)}</td>
                      </tr>
                    );
                    firstRow = false;
                  }
                });
                out.push(
                  <tr key={day + "-total"} style={{ background: "#f8fafc" }}>
                    <td style={{ ...td, borderBottom: BB }}></td>
                    <td style={{ ...td, borderBottom: BB, fontWeight: 800 }} colSpan={3}>{fmtDate(day)} TOPLAM</td>
                    <td style={{ ...td, ...R, borderBottom: BB, fontWeight: 800 }} className="num">{fmtAmt(dayDebit)}</td>
                  </tr>
                );
              }
              return out;
            })()}
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "#64748b", padding: 20 }}>Bu dönemde hareket yok</td></tr>}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f1f5f9" }}><td style={{ ...td, fontWeight: 800 }} colSpan={4}>GENEL TOPLAM (Borç)</td><td style={{ ...td, ...R, fontWeight: 800 }} className="num">{fmtAmt(totDebit)}</td></tr>
            <tr style={{ background: "#f1f5f9" }}><td style={{ ...td, fontWeight: 800 }} colSpan={4}>TOPLAM TAHSİLAT</td><td style={{ ...td, ...R, fontWeight: 800, color: "#059669" }} className="num">{fmtAmt(totCredit)}</td></tr>
            <tr style={{ background: "#fef9c3" }}><td style={{ ...td, fontWeight: 800 }} colSpan={4}>GÜNCEL BAKİYE</td><td style={{ ...td, ...R, fontWeight: 800, color: closing > 0 ? "#E05A47" : "#059669" }} className="num">{fmtAmt(closing)}</td></tr>
          </tfoot>
        </table>

        <div style={{ marginTop: 24, fontSize: "8.5pt", color: "#64748b", borderTop: "1px solid #e2e8f0", paddingTop: 10 }}>
          {closing > 0 ? `Firmanın ${fmtAmt(closing)} tutarında borcu bulunmaktadır.` : closing < 0 ? `Firmanın ${fmtAmt(-closing)} tutarında alacağı bulunmaktadır.` : "Hesap bakiyesi sıfırdır."}
          {settings.footer_text && <div style={{ marginTop: 6 }}>{settings.footer_text}</div>}
        </div>
      </div>
    </div>
  );
}
